import { Box, Button, Typography } from "@outschool/backpack";
import { PaymentMethod } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import {
  Modal,
  ModalActionButtons
} from "@outschool/ui-legacy-component-library";
import { pxToRem } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";
import { Trans } from "react-i18next";

import { useDeletePaymentMethod } from "../../hooks/CurrentUser";
import ErrorMessage from "../ErrorMessage";
import LoadingWithMessage from "../LoadingWithMessage";
import { useDefaultStripeErrorMessage } from "./Stripe";

function RemovePaymentMethodModal({
  paymentMethod,
  open,
  onClose
}: {
  paymentMethod: Pick<
    PaymentMethod,
    "uid" | "brand" | "last4" | "expirationMonth" | "expirationYear"
  >;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards"
  );
  const [
    deletePaymentMethod,
    { error: deletePaymentMethodError, loading: deletePaymentMethodLoading }
  ] = useDeletePaymentMethod();
  const errorMessage = useDefaultStripeErrorMessage();

  return (
    <Modal open={open} onClose={onClose}>
      <Typography variant="h3">
        {t("Remove credit card from account?")}
      </Typography>
      <Box
        sx={{
          marginBottom: "1em"
        }}
      >
        <Trans t={t}>
          Are you sure you want to remove{" "}
          <PaymentMethodLineItem paymentMethod={paymentMethod} /> from your
          account? Any active subscriptions will be canceled on Sunday unless
          you add another card to your profile before then.
        </Trans>
      </Box>
      {deletePaymentMethodError && <ErrorMessage value={errorMessage} />}
      {deletePaymentMethodLoading ? (
        <LoadingWithMessage>
          {t`Please wait while we remove the credit card from your account.`}
        </LoadingWithMessage>
      ) : (
        <ModalActionButtons
          disabled={deletePaymentMethodLoading}
          primaryVariant="danger"
          primaryLabel={t`Remove card`}
          onPrimary={async () => {
            await deletePaymentMethod(paymentMethod.uid);
            onClose();
          }}
          onCancel={() => onClose()}
        />
      )}
    </Modal>
  );
}

function PaymentMethodLineItem({
  paymentMethod
}: {
  paymentMethod: Pick<
    PaymentMethod,
    "brand" | "last4" | "expirationMonth" | "expirationYear"
  >;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards"
  );
  return (
    <React.Fragment>
      {_.capitalize(paymentMethod.brand)} **** {paymentMethod.last4} {t("Exp.")}{" "}
      {/* eslint-disable-next-line i18next/no-literal-string */}
      {paymentMethod.expirationMonth.toFixed().padStart(2, "0")}/
      {paymentMethod.expirationYear.toString().substring(2)}
    </React.Fragment>
  );
}

type SavedCardViewProps = {
  defaultPaymentMethod: PaymentMethod;
  editCallback?: () => void;
  resumeYourSubscriptionsCallback?: () => void;
  updatePaymentMethodDueToPaymentFailure?: boolean;
  disabled?: boolean;
};

export default function StripeSavedCards({
  defaultPaymentMethod,
  editCallback,
  resumeYourSubscriptionsCallback,
  updatePaymentMethodDueToPaymentFailure = false,
  disabled = false
}: SavedCardViewProps) {
  return editCallback ? (
    <EditSavedCards
      defaultPaymentMethod={defaultPaymentMethod}
      editCallback={editCallback}
      disabled={disabled}
    />
  ) : (
    <RemoveSavedCards
      defaultPaymentMethod={defaultPaymentMethod}
      resumeYourSubscriptionsCallback={resumeYourSubscriptionsCallback}
      updatePaymentMethodDueToPaymentFailure={
        updatePaymentMethodDueToPaymentFailure
      }
      disabled={disabled}
    />
  );
}
function EditSavedCards({
  defaultPaymentMethod,
  editCallback,
  disabled
}: {
  defaultPaymentMethod: PaymentMethod;
  editCallback: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards"
  );

  return (
    <Box
      sx={{
        borderTop: "1px solid #E3E3E3",
        borderBottom: "1px solid #E3E3E3",
        marginTop: "0.5em",
        marginBottom: "2em"
      }}
    >
      <Box
        flex
        sx={{
          justifyContent: "space-between",
          py: "0.5em"
        }}
      >
        <PaymentMethodLineItem paymentMethod={defaultPaymentMethod} />
        <Button variant="link" onClick={editCallback} disabled={disabled}>
          {t("Edit")}
        </Button>
      </Box>

      <Box
        sx={{
          marginBottom: "1em",
          padding: "0.5em",
          borderRadius: pxToRem(8),
          backgroundColor: "grey.50",
          fontSize: "0.875em"
        }}
      >
        {t(
          "Editing a saved card will change the card for all active enrollments."
        )}
      </Box>
    </Box>
  );
}

function RemoveSavedCards({
  defaultPaymentMethod,
  disabled,
  resumeYourSubscriptionsCallback = () => {},
  updatePaymentMethodDueToPaymentFailure
}: {
  defaultPaymentMethod: PaymentMethod;
  resumeYourSubscriptionsCallback?: () => void;
  updatePaymentMethodDueToPaymentFailure: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards"
  );
  const [showRemoveCardModal, setShowRemoveCardModal] = React.useState(false);
  const [
    deletePaymentMethod,
    { error: deletePaymentMethodError, loading: deletePaymentMethodLoading }
  ] = useDeletePaymentMethod();
  const errorMessage = useDefaultStripeErrorMessage();

  if (updatePaymentMethodDueToPaymentFailure) {
    // If we are updating payment method due to a payment failure, use different flow that replaces the "Remove" CTA with "Try Again" and "Replace with New Card"
    // "Replace with New Card" does the same thing as "Remove" but skips the nested "Confirm removal" modal and removes the payment method immediately
    return deletePaymentMethodLoading ? (
      <LoadingWithMessage>
        {t`Please wait while we remove the credit card from your account.`}
      </LoadingWithMessage>
    ) : (
      <Box flex sx={{ flexDirection: "column" }}>
        <Box
          flex
          sx={{
            borderTop: "1px solid #E3E3E3",
            justifyContent: "space-between",
            py: "0.5em",
            borderBottom: "1px solid #E3E3E3"
          }}
        >
          <PaymentMethodLineItem paymentMethod={defaultPaymentMethod} />
          <Box flex sx={{ gap: "24px", justifyContent: "end" }}>
            <Button
              variant="link"
              color="error"
              onClick={async () => {
                resumeYourSubscriptionsCallback();
              }}
              disabled={disabled}
            >
              {t("Try Again")}
            </Button>
            <Button
              variant="link"
              color="primary"
              onClick={async () => {
                // We delete the payment method immediately and skip the RemovePaymentMethodModal following new designs
                await deletePaymentMethod(defaultPaymentMethod.uid);
              }}
              disabled={disabled}
            >
              {t("Replace with New Card")}
            </Button>
          </Box>
        </Box>
        {deletePaymentMethodError && <ErrorMessage value={errorMessage} />}
      </Box>
    );
  } else {
    return (
      // This is the non-payment failure flow (currently in prod), where we open another modal on top and users have to confirm the removal
      <Box>
        <Button
          variant="link"
          color="error"
          onClick={() => setShowRemoveCardModal(true)}
          disabled={disabled}
        >
          {t("Remove")}
        </Button>
        <RemovePaymentMethodModal
          paymentMethod={defaultPaymentMethod}
          open={showRemoveCardModal}
          onClose={() => setShowRemoveCardModal(false)}
        />
      </Box>
    );
  }
}
